import React, { useEffect, useState } from "react";
import {
  Button,
  FormControl,
  IconButton,
  InputLabel,
  Select,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import CommonDataGrid from "../../../components/datagrid/CommonDataGrid";
import { useDispatch, useSelector } from "react-redux";
import {
  adjustProductDetails,
  deleteProduct,
  getProductsWithPagination,
  searchProductsByName,
  searchProductsBySerial,
} from "../../../redux/product/productSlice";
import { Menu, MenuItem } from "@mui/material";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import toast from "react-hot-toast";
import QuickPurchaseDialog from "./QuickPurchaseDialog";
import useScanDetection from "use-scan-detection";
import ScannerDialog from "./ScannerDialog";
import { useTranslation } from "react-i18next";
import { Input } from "antd";
import { ROWS_PER_PAGE } from "../../../actionTypes";

const ProductList = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [search, setSearch] = useState("");
  const [searchBySerial, setSearchBySerial] = useState("");
  const [searchTrigger, setSearchTrigger] = useState(0);
  const rowsPerPage = ROWS_PER_PAGE;
  const [anchorElMap, setAnchorElMap] = useState({});
  const [deleteStatus, setDeleteStatus] = useState(false);

  const [openDialog, setOpenDialog] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);

  const [scannedData, setScannedData] = useState(null); // State to store scanned data

  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [productToDelete, setProductToDelete] = useState(null);

  const handleQuickPurchaseClick = (data) => {
    setSelectedProduct(data);
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    window.location.reload();
  };

  const handleClick = (event, rowData) => {
    setAnchorElMap((prevMap) => ({
      ...prevMap,
      [rowData.id]: event.currentTarget,
    }));
  };

  const handleClose = (rowData) => {
    setAnchorElMap((prevMap) => ({
      ...prevMap,
      [rowData.id]: null,
    }));
  };

  const isAdjustmentNeeded = (rowData) => {
    if (!rowData) return false;
    const missingSerial =
      rowData.serial_no === null ||
      rowData.serial_no === undefined ||
      String(rowData.serial_no).trim() === "" ||
      Number(rowData.serial_no) <= 0;
    const incompleteItem =
      !rowData.product_name ||
      !rowData.unit_id ||
      !rowData.purchase_unit_id ||
      !rowData.sale_unit_id ||
      !rowData.product_category_id ||
      Number(rowData.needs_adjustment) === 1 ||
      rowData.needs_adjustment === true;
    return missingSerial || incompleteItem;
  };

  const handleAdjustItemDetailsClick = async (rowData) => {
    handleClose(rowData);
    try {
      const response = await dispatch(adjustProductDetails(rowData.id));
      if (
        response &&
        response.payload &&
        response.payload.statusCode === 200
      ) {
        toast.success(
          (response.payload && response.payload.message) ||
            "Item details adjusted successfully"
        );
        setSearchTrigger((prev) => prev + 1);
      } else {
        toast.error(
          (response &&
            response.payload &&
            response.payload.message) ||
            "Failed to adjust item details"
        );
      }
    } catch (error) {
      console.error("Error adjusting item details:", error);
      toast.error("Failed to adjust item details");
    }
  };

  const renderActionsColumn = (rowData) => {
    return (
      <>
        <IconButton onClick={(event) => handleClick(event, rowData)}>
          <MoreVertIcon />
        </IconButton>
        <Menu
          anchorEl={anchorElMap[rowData.id]}
          open={Boolean(anchorElMap[rowData.id])}
          keepMounted
          onClose={() => handleClose(rowData)}
          sx={{
            boxShadow:
              "0px 7px 8px -4px rgb(0 0 0 / 20%), 0px 12px 17px 2px rgb(0 0 0 / 14%), 0px 5px 22px 4px rgb(0 0 0 / 12%)",
          }}
        >
          {isAdjustmentNeeded(rowData) && (
            <MenuItem
              sx={{ fontSize: "14px", fontWeight: 600, color: "#d97706" }}
              onClick={() => handleAdjustItemDetailsClick(rowData)}
            >
              {t("Adjust Item Details")}
            </MenuItem>
          )}
          <MenuItem
            sx={{ fontSize: "14px" }}
            onClick={() => handleEditClick(rowData.id)}
          >
            {t("Edit")}
          </MenuItem>
          <MenuItem
            sx={{ fontSize: "14px" }}
            onClick={() => handleDeleteClick(rowData.id)}
          >
            {t("Delete")}
          </MenuItem>
          <MenuItem
            sx={{ fontSize: "14px" }}
            onClick={() => handleQuickPurchaseClick(rowData)}
          >
            {t("Quick Purchase")}
          </MenuItem>
        </Menu>
      </>
    );
  };
  const columns = [
    { columnName: "product_name", columnShow: "product_name" },
    { columnName: "product_code", columnShow: "product_code" },
    { columnName: "serial_no", columnShow: "serial_no" },
    { columnName: "weight", columnShow: "weight" },
    { columnName: "unit_name", columnShow: "unit_name" },
    { columnName: "PurchaseStatus", columnShow: "Purchase Status" },
    {
      columnName: "actions",
      columnShow: "Actions",
      render: renderActionsColumn,
    },
  ];

  const cellFormatting = {
    product_name: {
      textAlign: "left",
    },
    product_code: {
      textAlign: "left",
    },
    weight: {
      textAlign: "left",
    },
    unit_name: {
      textAlign: "left",
    },
    actions: {
      width: "100px",
      textAlign: "center",
    },
  };
  const manipulateData = (data) => {
    // Perform data manipulation here
    return data.map((item) => ({
      ...item,
    }));
  };

  const handleEditClick = (id) => {
    navigate(`/product/${id}`, { replace: true });
  };

  const handleDeleteClick = (id) => {
    setProductToDelete(id);
    setDeleteDialogOpen(true);
    handleClose({ id }); // Close the menu
  };

  const handleConfirmDelete = async () => {
    if (!productToDelete) return;

    const res = await dispatch(deleteProduct(productToDelete))
      .then((response) => {
        if (
          response &&
          response.payload &&
          response.payload.statusCode === 200
        ) {
          toast.success(
            response && response.payload && response.payload.message
          );

          window.location.reload();
        }
      })
      .catch((error) => {
        console.error("Error submitting form:", error);
        toast.error(error);
      });

    setDeleteDialogOpen(false);
    setProductToDelete(null);
  };

  useEffect(() => {
    const handler = setTimeout(() => {
      setSearchTrigger((prev) => prev + 1);
    }, 1000); // 1 second debounce

    return () => {
      clearTimeout(handler);
    };
  }, [search, searchBySerial]);

  const handleSearch = (e) => {
    setSearch(e.target.value);
    // setSearchTrigger((prev) => prev + 1);
  };

  const handleSerialSearch = (e) => {
    setSearchBySerial(e.target.value);
    // setSearchTrigger((prev) => prev + 1);
  };

  const fetchData = async (page = 0, pageSize = rowsPerPage) => {
    let response;
    
    // Determine which API to call based on search criteria
    if (searchBySerial && searchBySerial.trim() !== "") {
       // Search by serial number
       response = await dispatch(
         searchProductsBySerial({
           page: page - 1,
           pageSize: pageSize,
           searchBySerial: searchBySerial.trim(),
         })
       );
    } else if (search && search.trim() !== "") {
       // Search by product name
       response = await dispatch(
         searchProductsByName({
           page: page - 1,
           pageSize: pageSize,
           search: search.trim(),
         })
       );
    } else {
      // Basic pagination without search
      response = await dispatch(
        getProductsWithPagination({
          page: page - 1,
          pageSize: pageSize,
        })
      );
    }

    const data = (await response.payload) && response.payload.data;
    const totalCount =
      data && typeof data.total === "object" && data.total !== null
        ? Number(data.total.total) || 0
        : Number(data && data.total) || 0;
    return {
      data: (data && data.rows) || [],
      total: totalCount,
      totalPages: (totalCount && Math.ceil(totalCount / rowsPerPage)) || 0,
    };
  };

  useEffect(() => {
    if (deleteStatus) {
      fetchData(1); // Fetch data again after deletion
      setDeleteStatus(false); // Reset delete status
    }
  }, [deleteStatus]);

  const [scannedId, setScannedId] = useState(null);
  useScanDetection({
    onComplete: setScannedData,
  });

  if (scannedData) {
    return navigate("/scan-product", {
      state: { data: scannedData },
      replace: true,
    });
  }

  return (
    <>
      <div className="p-4">
        <div className="w-full flex items-center justify-between mb-6">
          <Typography variant="h6">{t("Product List")}</Typography>
          <Button
            variant="contained"
            color="secondary"
            onClick={() => navigate("/product/new", { replace: true })}
          >
            {t("Add Product")}
          </Button>
        </div>
        <div className="flex flex-col md:flex-row gap-5">
          <Input
            className="mb-6 max-w-sm"
            value={search}
            onChange={handleSearch}
            placeholder="Search product..."
          />
          <Input
            className="mb-6 max-w-sm"
            value={searchBySerial}
            onChange={handleSerialSearch}
            placeholder="Search product by serial no..."
          />
        </div>
        <CommonDataGrid
          columns={columns}
          fetchData={fetchData}
          cellFormatting={cellFormatting}
          dataManipulator={manipulateData}
          key={searchTrigger}
        />
      </div>
      <QuickPurchaseDialog
        open={openDialog}
        onClose={handleCloseDialog}
        selectedProduct={selectedProduct}
      />
      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
      >
        <DialogTitle>{t("Confirm Delete")}</DialogTitle>
        <DialogContent>
          {t("Are you sure you want to delete this product?")}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>
            {t("Cancel")}
          </Button>
          <Button
            onClick={handleConfirmDelete}
            color="error"
            variant="contained"
          >
            {t("Delete")}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default ProductList;
